import { Line, LineSvgProps, Serie } from '@nivo/line'
import { TooltipAnchor, useTooltip } from '@nivo/tooltip'
import { Mesh } from '@nivo/voronoi'
import { format, getTime } from 'date-fns'
import { ko } from 'date-fns/locale'
import { useEffect, useRef, useState } from 'react'
import { CAF_ANALYSIS_TYPE_MAP, Trend } from '../../../../model/caf.interface'
import { GRAPH_TOOLTIP_CLS, graph_common_props } from '../../../../modules/consts/caf'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import { axisRange } from '../../../../modules/hooks/webview/caf/chart/utilFunction'
import { n_caf } from '../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { COLORS } from '../../../../modules/vars'
import Skeleton from '../../../skeleton'
import CommonLegendList, { LEGEND_SHAPE } from '../charts/common/CommonLegendList'

const today = new Date()

interface IDataType {
  x: number
  y: number
  color: string
}

interface IGraphData extends Serie {
  id: string | number
  color: string
  data: IDataType[]
}

interface ITrendGraphProps {
  trend: Trend
  activeFilter: string
  showDetail?: boolean
  showSkeleton?: boolean
}

const createData = (trend: Trend, category: string, maxNum: number) => {
  let data: IDataType[] = []
  data = trend[category]?.map((d) => {
    return {
      x: getTime(new Date(d.start_time)),
      y: d.level,
    }
  })
  const emptyDataSet = (num) =>
    Array.from({ length: num }, (v, k) => ({
      x: getTime(today) + k,
      y: null,
      color: 'transparent',
    }))
  if (trend[category]?.length < maxNum) {
    data = data.concat(emptyDataSet(maxNum - trend[category].length) as any)
  }
  return data
}

const reverseData = (array: any[]) => {
  array.reverse()
  return [...array]
}

const yRange = reverseData(axisRange(9, 3, 1))
const CHART_STYLE: Partial<LineSvgProps> = {
  curve: 'monotoneX',
  margin: { bottom: 20, left: 16, top: 20, right: 12 },
  role: 'presentation',
  pointBorderWidth: 2,
  pointColor: 'white',
  pointBorderColor: {
    from: 'serieColor',
  },
  animate: true,
  pointSize: 7,
  gridYValues: yRange,
  enableGridX: false,
  yScale: { type: 'linear', min: 0, max: 9 },
  theme: graph_common_props,
  useMesh: true, // @nivo/line에서 useMesh가 false면 mouse event 작동 X -> tooltip 작동 X => layers에 CustomMesh 넣어서 dash 삭제
  // role: "application",
}

const activeGraphColors = {
  complexity: COLORS.BLUE,
  accuracy: COLORS.RED,
  fluency: COLORS.GREEN,
  pronunciation: COLORS.YELLOW,
}
const inactiveGraphColors = {
  complexity: 'rgba(68, 149, 255, 0.3)',
  accuracy: 'rgba(245, 52, 40, 0.3)',
  fluency: 'rgba(43, 171, 99, 0.3)',
  pronunciation: 'rgba(255, 179, 0, 0.3)',
}

const TrendGraphSkeleton = () => {
  return (
    <div className="">
      <div className="flex w-full flex-row justify-between">
        <Skeleton.Rectangular w={96} h={24} className="hidden sm:block" />
        <Skeleton.Rectangular w={212} h={24} />
      </div>
      <Skeleton.Rectangular w={'100%'} h={140} className="mt-[12px]" />
      <div className="mt-[16px] flex w-full flex-row items-center justify-center">
        <Skeleton.Rectangular w={160} h={20} />
      </div>
      <div className="mt-[8px] flex w-full flex-row items-center justify-center gap-[16px]">
        <Skeleton.Rectangular w={72} h={20} />
        <Skeleton.Rectangular w={72} h={20} />
        <Skeleton.Rectangular w={72} h={20} />
        <Skeleton.Rectangular w={72} h={20} />
      </div>
    </div>
  )
}
const TrendGraph = ({ trend, activeFilter, showDetail, showSkeleton = false }: ITrendGraphProps) => {
  const { t, isLocaleKO } = useTranslation()
  const isMobile = useIsMobile()
  const hasPronunciation = Array.isArray(trend?.pronunciation)
  const chartWrapperRef = useRef()
  const [graphData, setGraphData] = useState<IGraphData[]>([])
  const LABEL_LIST = [
    {
      label: t(n_caf.overview.complexity),
      color: activeGraphColors[CAF_ANALYSIS_TYPE_MAP[1]],
      type: LEGEND_SHAPE.round,
    },
    { label: t(n_caf.overview.accuracy), color: activeGraphColors[CAF_ANALYSIS_TYPE_MAP[2]], type: LEGEND_SHAPE.round },
    { label: t(n_caf.overview.fluency), color: activeGraphColors[CAF_ANALYSIS_TYPE_MAP[3]], type: LEGEND_SHAPE.round },
    {
      label: t(n_caf.overview.pronunciation),
      color: activeGraphColors[CAF_ANALYSIS_TYPE_MAP[4]],
      type: LEGEND_SHAPE.round,
    },
  ]
  useEffect(() => {
    const nextData = hasPronunciation
      ? [
          {
            id: CAF_ANALYSIS_TYPE_MAP[1],
            color: COLORS.BLUE,
            data: createData(trend, CAF_ANALYSIS_TYPE_MAP[1], 12),
          },
          {
            id: CAF_ANALYSIS_TYPE_MAP[2],
            color: COLORS.RED,
            data: createData(trend, CAF_ANALYSIS_TYPE_MAP[2], 12),
          },
          {
            id: CAF_ANALYSIS_TYPE_MAP[3],
            color: COLORS.GREEN,
            data: createData(trend, CAF_ANALYSIS_TYPE_MAP[3], 12),
          },
          {
            id: CAF_ANALYSIS_TYPE_MAP[4],
            color: COLORS.GREEN,
            data: createData(trend, CAF_ANALYSIS_TYPE_MAP[4], 12),
          },
        ]
      : [
          {
            id: CAF_ANALYSIS_TYPE_MAP[1],
            color: COLORS.BLUE,
            data: createData(trend, CAF_ANALYSIS_TYPE_MAP[1], 12),
          },
          {
            id: CAF_ANALYSIS_TYPE_MAP[2],
            color: COLORS.RED,
            data: createData(trend, CAF_ANALYSIS_TYPE_MAP[2], 12),
          },
          {
            id: CAF_ANALYSIS_TYPE_MAP[3],
            color: COLORS.GREEN,
            data: createData(trend, CAF_ANALYSIS_TYPE_MAP[3], 12),
          },
        ]
    setGraphData(nextData)
  }, [trend, showDetail, isMobile])

  const levels = hasPronunciation
    ? {
        complexity: trend[CAF_ANALYSIS_TYPE_MAP[1]]?.map((item) => item.level),
        accuracy: trend[CAF_ANALYSIS_TYPE_MAP[2]]?.map((item) => item.level),
        fluency: trend[CAF_ANALYSIS_TYPE_MAP[3]]?.map((item) => item.level),
        pronunciation: trend[CAF_ANALYSIS_TYPE_MAP[4]]?.map((item) => item.level),
      }
    : {
        complexity: trend[CAF_ANALYSIS_TYPE_MAP[1]]?.map((item) => item.level),
        accuracy: trend[CAF_ANALYSIS_TYPE_MAP[2]]?.map((item) => item.level),
        fluency: trend[CAF_ANALYSIS_TYPE_MAP[3]]?.map((item) => item.level),
      }
  const [selectedPointX, setSelectedPointX] = useState<number>()
  const handleClick = (point) => {
    setSelectedPointX(point?.data?.x)
  }

  const ToolTip = ({ data }) => {
    return data ? (
      <div className={GRAPH_TOOLTIP_CLS}>
        {isLocaleKO ? (
          <div className="Body2-14Md">{format(data, 'M월 d일(EE) a hh:mm', { locale: ko })}</div>
        ) : (
          <div className="Body2-14Md">{format(data, 'EEE, MMM d hh:mma')}</div>
        )}
      </div>
    ) : (
      <></>
    )
  }

  const CustomMesh = (layerData: any) => {
    const { showTooltipAt, hideTooltip } = useTooltip()
    const handleTooltip = (point: any) => {
      let coordinates: [number, number] = [point.x + layerData.margin.right, point.y + layerData.margin.bottom]
      let position: TooltipAnchor = 'right'
      const index = Number(point?.id?.split('.')?.[1])
      if (graphData?.length && graphData[0].data.length < index + 3) {
        coordinates = [point.x + layerData.margin.left, point.y + layerData.margin.bottom]
        position = 'left'
      }
      showTooltipAt(layerData.tooltip({ point }), coordinates, position)
    }
    const handleMouseEnter = (point: any) => {
      point.data?.y && handleTooltip(point)
    }
    const handleMouseMove = (point: any) => {
      point.data?.y && handleTooltip(point)
    }
    const handleMouseLeave = (point: any) => {
      hideTooltip()
      setSelectedPointX(null)
    }
    const nullValuePoints = layerData.series.reduce((acc: any[], cur: any) => {
      cur.data.forEach(({ data, position }: any) => {
        if (data.y === null) {
          const point = {
            x: position.x,
            y: 100,
            data: {
              x: data.x,
            },
          }
          acc.push(point)
        }
      })
      return acc
    }, [])
    return (
      <Mesh
        nodes={[...layerData.points, ...nullValuePoints]}
        width={layerData.width}
        height={layerData.height}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        debug={layerData.debugMesh}
      />
    )
  }
  const GRAPH_DATA_INDEX_MAP = {
    all: 0,
    complexity: 0,
    accuracy: 1,
    fluency: 2,
    pronunciation: 3,
  }

  if (showSkeleton) return <TrendGraphSkeleton />
  if (isMobile) {
    return (
      <div>
        <div className="mt-[12px] flex items-center justify-center">
          <div className={styles.yaxis}>
            {yRange?.map((y) => (
              <div key={y}>{y}</div>
            ))}
          </div>
          <div className={styles.middle.box}>
            <Line
              height={160}
              width={graphData?.length && graphData[GRAPH_DATA_INDEX_MAP[activeFilter]]?.data.length * 60}
              data={activeFilter === 'all' ? graphData : [graphData.find((g) => g.id === activeFilter)]}
              colors={
                activeFilter === 'all'
                  ? !selectedPointX
                    ? Object.values(activeGraphColors)
                    : Object.values(inactiveGraphColors)
                  : !selectedPointX
                  ? [activeGraphColors[activeFilter]]
                  : [inactiveGraphColors[activeFilter]]
              }
              layers={['grid', 'lines', 'axes', 'points', CustomMesh]}
              {...CHART_STYLE}
              tooltip={({ point }) => {
                return <ToolTip data={new Date(point.data.x)} />
              }}
              axisLeft={{
                tickValues: null,
                renderTick: (tick) => <></>,
              }}
              axisBottom={{
                tickSize: 0,
                legendPosition: 'start',
                renderTick: (tick) => <CustomXTick tick={tick} color={COLORS.GRAY500} />,
              }}
              enablePointLabel={activeFilter !== 'all'}
              pointBorderColor={(object) => {
                let graphDataIndex = 0
                if (activeFilter === 'all') {
                  if (object.serieId === CAF_ANALYSIS_TYPE_MAP[2]) {
                    graphDataIndex = 1
                  } else if (object.serieId === CAF_ANALYSIS_TYPE_MAP[3]) {
                    graphDataIndex = 2
                  } else if (object.serieId === CAF_ANALYSIS_TYPE_MAP[4]) {
                    graphDataIndex = 3
                  }
                }
                const index = object.index - trend[object.serieId]?.length * graphDataIndex
                const x = graphData[graphDataIndex]?.data[index]?.x
                return activeFilter === 'all'
                  ? selectedPointX && x !== selectedPointX
                    ? inactiveGraphColors[object.serieId]
                    : activeGraphColors[object.serieId]
                  : selectedPointX && x !== selectedPointX
                  ? inactiveGraphColors[activeFilter]
                  : activeGraphColors[activeFilter]
              }}
            />
          </div>
        </div>
        <div className={styles.bottom.title}>{t(n_caf.statistic.highlight.graph_date)}</div>
        {activeFilter === 'all' ? (
          <CommonLegendList list={LABEL_LIST} />
        ) : (
          <div className={styles.bottom.box}>
            <div className={styles.bottom.item}>
              <span className={styles.bottom.key}>{t(n_caf.statistic.trend.min)}</span>
              <span className={styles.bottom.value}>
                {levels[activeFilter] && isFinite(Math.min(...levels[activeFilter]))
                  ? Math.min(...levels[activeFilter])
                  : 0}
              </span>
            </div>
            <div className={styles.bottom.item}>
              <span className={styles.bottom.key}>{t(n_caf.statistic.trend.max)}</span>
              <span className={styles.bottom.value}>
                {levels[activeFilter] && isFinite(Math.max(...levels[activeFilter]))
                  ? Math.max(...levels[activeFilter])
                  : 0}
              </span>
            </div>
          </div>
        )}
      </div>
    )
  } else {
    return (
      <div>
        <div className="mt-[12px] flex items-center justify-center">
          <div className={styles.yaxis_pc}>
            {yRange?.map((y) => (
              <div key={y}>{y}</div>
            ))}
          </div>
          <div className={styles.middle.box_pc} ref={chartWrapperRef}>
            <Line
              height={160}
              width={graphData?.length && graphData[GRAPH_DATA_INDEX_MAP[activeFilter]]?.data?.length * 70}
              data={activeFilter === 'all' ? [...graphData] : [graphData.find((g) => g.id === activeFilter)]}
              colors={
                activeFilter === 'all'
                  ? !selectedPointX
                    ? Object.values(activeGraphColors)
                    : Object.values(inactiveGraphColors)
                  : !selectedPointX
                  ? [activeGraphColors[activeFilter]]
                  : [inactiveGraphColors[activeFilter]]
              }
              layers={['grid', 'lines', 'axes', 'points', CustomMesh]}
              {...CHART_STYLE}
              tooltip={({ point }) => {
                return <ToolTip data={new Date(point.data.x)} />
              }}
              axisLeft={{
                tickValues: null,
                renderTick: (tick) => <></>,
              }}
              axisBottom={{
                tickSize: 0,
                legendPosition: 'start',
                renderTick: (tick) => <CustomXTick tick={tick} color={COLORS.GRAY500} />,
              }}
              enablePointLabel={activeFilter !== 'all'}
              pointBorderColor={(object) => {
                let graphDataIndex = 0
                if (activeFilter === 'all') {
                  if (object.serieId === CAF_ANALYSIS_TYPE_MAP[2]) {
                    graphDataIndex = 1
                  } else if (object.serieId === CAF_ANALYSIS_TYPE_MAP[3]) {
                    graphDataIndex = 2
                  } else if (object.serieId === CAF_ANALYSIS_TYPE_MAP[4]) {
                    graphDataIndex = 3
                  }
                }
                const index = object.index - trend[object.serieId]?.length * graphDataIndex
                const x = graphData[graphDataIndex]?.data[index]?.x
                return activeFilter === 'all'
                  ? selectedPointX && x !== selectedPointX
                    ? inactiveGraphColors[object.serieId]
                    : activeGraphColors[object.serieId]
                  : selectedPointX && x !== selectedPointX
                  ? inactiveGraphColors[activeFilter]
                  : activeGraphColors[activeFilter]
              }}
            />
          </div>
        </div>
        <div className={styles.bottom.title_pc}>{t(n_caf.statistic.highlight.graph_date)}</div>
        {activeFilter === 'all' ? (
          <CommonLegendList list={LABEL_LIST} />
        ) : (
          <div className={styles.bottom.box}>
            <div className={styles.bottom.item}>
              <span className={styles.bottom.key}>{t(n_caf.statistic.trend.min)}</span>
              <span className={styles.bottom.value}>
                {levels[activeFilter] && isFinite(Math.min(...levels[activeFilter]))
                  ? Math.min(...levels[activeFilter])
                  : 0}
              </span>
            </div>
            <div className={styles.bottom.item}>
              <span className={styles.bottom.key}>{t(n_caf.statistic.trend.max)}</span>
              <span className={styles.bottom.value}>
                {levels[activeFilter] && isFinite(Math.max(...levels[activeFilter]))
                  ? Math.max(...levels[activeFilter])
                  : 0}
              </span>
            </div>
          </div>
        )}
      </div>
    )
  }
}

export default TrendGraph

const styles = {
  yaxis: `flex h-[152px] flex-col justify-between pb-[16px] text-[12px] text-gray-500`,
  yaxis_pc: `flex h-[152px] flex-col justify-between pb-[16px] text-[14px] text-gray-500`,
  middle: {
    box: `scrollbar-hide h-[180px] w-full overflow-x-scroll bg-white`,
    box_pc: `scrollbar-hide h-[180px] w-[688px] overflow-x-scroll bg-white`,
  },
  bottom: {
    title: `text-h5 text-center font-medium text-gray-600`,
    title_pc: `text-h5 mt-[9px] text-center font-medium text-gray-600`,
    box: `mt-[12px] flex items-center justify-around rounded bg-gray-50 py-[8px]`,
    item: `not-first:border-l flex h-[24px] flex-1 items-center justify-center gap-[16px] border-gray-200`,
    key: `text-[14px] leading-[24px]`,
    value: `font-bold leading-[24px]`,
  },
}

const CustomXTick = ({ tick, color }) => {
  const xtick = tick?.value < getTime(today) ? new Date(tick.value) : null
  const isMobile = useIsMobile()
  if (isMobile) {
    return (
      <g transform={`translate(${tick.x},${tick.y + 16})`}>
        <text
          textAnchor="middle"
          dominantBaseline="middle"
          style={{
            fill: color,
            fontSize: 12,
          }}
        >
          {xtick ? format(xtick, 'M.dd') : null}
        </text>
      </g>
    )
  } else {
    return (
      <g transform={`translate(${tick.x},${tick.y + 16})`}>
        <text
          textAnchor="middle"
          dominantBaseline="middle"
          style={{
            fill: color,
            fontSize: 14,
          }}
        >
          {xtick ? format(xtick, 'M.dd') : null}
        </text>
      </g>
    )
  }
}
